// components/BulkUploadClientIds.tsx
import React, { useState, useCallback } from 'react';
import { ExcelFileUpload } from './UploadFile';
import { getAuth } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import CustomButton from './CustomButton';

const SERVER_URL = "https://us-central1-elisyanwealth.cloudfunctions.net/";
const BASE_URL =
  "http://127.0.0.1:5001/elisyanwealth/us-central1/";

// Bulk partnerClientId upload/delete migrated to the v2 endpoints
// (functions/v2/endpoints/partnerClientIdEndpoints.ts → bulkUploadPartnerClientIds_v2 /
// bulkDeletePartnerClientIds_v2), which read/write `partnerClientIds_v2`.
// Pointed at BASE_URL (functions emulator) for pre-deploy testing — flip to
// SERVER_URL after deploying those functions.
const PARTNER_V2_DEPLOYED = true;
const PARTNER_V2_URL = PARTNER_V2_DEPLOYED ? SERVER_URL : BASE_URL;

interface Business {
  id: string;
  name: string;
}

interface ParsedClientId {
  clientId: string;
  name: string;
  type: 'Individual' | 'Business';
  users_limit: number;
  occurrences: number;
}

interface DeleteResult {
  clientId: string;
  name?: string;
  reason?: string;
  users_count?: number;
}

interface DeleteSummary {
  total_requested: number;
  deleted_count: number;
  not_found_count: number;
  has_users_count: number;
  error_count: number;
}

interface DeleteResponse {
  message: string;
  summary: DeleteSummary;
  deleted: DeleteResult[];
  not_found: DeleteResult[];
  has_users: DeleteResult[];
  errors?: DeleteResult[];
}

interface BulkUploadClientIdsProps {}

export const BulkUploadClientIds: React.FC<BulkUploadClientIdsProps> = ({}) => {
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [clientIds, setClientIds] = useState<ParsedClientId[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [deleteResult, setDeleteResult] = useState<DeleteResponse | null>(null);

  const location = useLocation();
  const businesses: Business[] = location.state?.businesses || [];

  const parseClientIdRow = useCallback(
    (row: Record<string, any>, headers: string[]): { clientId: string; name: string } | null => {
      const clientIdKey = headers.find(
        (h) =>
          (h.includes('client') && h.includes('code')) ||
          h === 'clientid' ||
          h === 'client_id' ||
          h === 'client code' ||
          h === 'code'
      );

      const nameKey = headers.find(
        (h) =>
          h.includes('name') ||
          h === 'clientname' ||
          h === 'client_name' ||
          h === 'client name'
      );

      const clientId = clientIdKey ? String(row[clientIdKey] || '').trim() : '';
      const name = nameKey ? String(row[nameKey] || '').trim() : '';

      if (!clientId) return null;

      return { clientId, name };
    },
    []
  );

  const processData = useCallback(
    (data: { clientId: string; name: string }[]): ParsedClientId[] => {
      // Group by clientId (case-insensitive)
      const groupedMap = new Map<string, { clientId: string; name: string }[]>();

      data.forEach((item) => {
        const key = item.clientId.toLowerCase();
        if (!groupedMap.has(key)) {
          groupedMap.set(key, []);
        }
        groupedMap.get(key)!.push(item);
      });

      const result: ParsedClientId[] = [];

      groupedMap.forEach((entries) => {
        const isUnique = entries.length === 1;
        result.push({
          clientId: entries[0].clientId,
          name: entries[0].name,
          type: isUnique ? 'Individual' : 'Business',
          users_limit: entries.length,
          occurrences: entries.length,
        });
      });

      return result;
    },
    []
  );

  const handleDataParsed = useCallback(
    (data: { clientId: string; name: string }[]) => {
      const processed = processData(data);
      setClientIds(processed);
      setError('');
      setSuccessMessage('');
      setDeleteResult(null);
    },
    [processData]
  );

  const handleFileSelect = useCallback((name: string) => {
    setFileName(name);
    setClientIds([]);
    setError('');
    setSuccessMessage('');
    setDeleteResult(null);
  }, []);

  const handleError = useCallback((errorMsg: string) => {
    setError(errorMsg);
  }, []);

  const getAuthToken = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    return user?.getIdToken();
  };

  const handleUpload = async () => {
    if (!selectedBusinessId) {
      setError('Please select a business');
      return;
    }

    if (clientIds.length === 0) {
      setError('No data to upload');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    setDeleteResult(null);

    try {
      const token = await getAuthToken();

      const response = await fetch(
        `${PARTNER_V2_URL}bulkUploadPartnerClientIds_v2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            businessId: selectedBusinessId,
            clientIds: clientIds.map((item) => ({
              clientId: item.clientId,
              name: item.name,
              type: item.type,
              users_limit: item.users_limit,
            })),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccessMessage(`Successfully uploaded ${data.count} client IDs`);
      setClientIds([]);
      setFileName('');
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload client IDs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBusinessId) {
      setError('Please select a business');
      return;
    }

    if (clientIds.length === 0) {
      setError('No data to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${clientIds.length} client ID(s)?\n\nOnly client IDs with 0 users will be deleted.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    setError('');
    setSuccessMessage('');
    setDeleteResult(null);

    try {
      const token = await getAuthToken();

      const response = await fetch(
        `${PARTNER_V2_URL}bulkDeletePartnerClientIds_v2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            businessId: selectedBusinessId,
            clientIds: clientIds.map((item) => item.clientId),
          }),
        }
      );

      const data: DeleteResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Delete failed');
      }

      setDeleteResult(data);

      if (data.summary.deleted_count > 0) {
        setSuccessMessage(
          `Successfully deleted ${data.summary.deleted_count} client ID(s)`
        );
      }

      if (data.summary.deleted_count === clientIds.length) {
        setClientIds([]);
        setFileName('');
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete client IDs');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClear = () => {
    setClientIds([]);
    setFileName('');
    setError('');
    setSuccessMessage('');
    setDeleteResult(null);
  };

  const isAnyLoading = isLoading || isDeleting;
  const uniqueCount = clientIds.filter((c) => c.type === 'Individual').length;
  const duplicateCount = clientIds.filter((c) => c.type === 'Business').length;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Bulk Manage Client IDs</h2>

      <div style={styles.field}>
        <label style={styles.label}>Select Business</label>
        <select
          value={selectedBusinessId}
          onChange={(e) => setSelectedBusinessId(e.target.value)}
          style={styles.select}
          disabled={isAnyLoading}
        >
          <option value="">Select a business...</option>
          {businesses.map((business) => (
            <option key={business.id} value={business.id}>
              {business.name}
            </option>
          ))}
        </select>
      </div>

      <ExcelFileUpload<{ clientId: string; name: string }>
        onDataParsed={handleDataParsed}
        onFileSelect={handleFileSelect}
        onError={handleError}
        parseRow={parseClientIdRow}
        disabled={isAnyLoading}
      >
        <div style={styles.uploadArea}>
          <div style={styles.uploadIcon}>📄</div>
          <div style={styles.uploadText}>
            {fileName || 'Click to select Excel file'}
          </div>
          <div style={styles.uploadHint}>Supports .xlsx, .xls, .csv</div>
        </div>
      </ExcelFileUpload>

      {error && <div style={styles.error}>{error}</div>}
      {successMessage && <div style={styles.success}>{successMessage}</div>}

      {/* Delete Result Summary */}
      {deleteResult && (
        <div style={styles.deleteResultContainer}>
          <h3 style={styles.deleteResultTitle}>Delete Summary</h3>

          <div style={styles.deleteStats}>
            <div style={styles.deleteStat}>
              <span style={styles.deleteStatNumber}>
                {deleteResult.summary.total_requested}
              </span>
              <span style={styles.deleteStatLabel}>Requested</span>
            </div>
            <div style={{ ...styles.deleteStat, ...styles.deleteStatSuccess }}>
              <span style={styles.deleteStatNumber}>
                {deleteResult.summary.deleted_count}
              </span>
              <span style={styles.deleteStatLabel}>Deleted</span>
            </div>
            <div style={{ ...styles.deleteStat, ...styles.deleteStatWarning }}>
              <span style={styles.deleteStatNumber}>
                {deleteResult.summary.has_users_count}
              </span>
              <span style={styles.deleteStatLabel}>Has Users</span>
            </div>
            <div style={{ ...styles.deleteStat, ...styles.deleteStatMuted }}>
              <span style={styles.deleteStatNumber}>
                {deleteResult.summary.not_found_count}
              </span>
              <span style={styles.deleteStatLabel}>Not Found</span>
            </div>
            {deleteResult.summary.error_count > 0 && (
              <div style={{ ...styles.deleteStat, ...styles.deleteStatError }}>
                <span style={styles.deleteStatNumber}>
                  {deleteResult.summary.error_count}
                </span>
                <span style={styles.deleteStatLabel}>Errors</span>
              </div>
            )}
          </div>

          {deleteResult.deleted.length > 0 && (
            <div style={styles.deleteResultSection}>
              <h4 style={styles.deleteResultSectionTitle}>
                ✓ Deleted ({deleteResult.deleted.length})
              </h4>
              <div style={styles.deleteResultList}>
                {deleteResult.deleted.map((item, idx) => (
                  <div key={idx} style={styles.deleteResultItem}>
                    <span style={styles.deleteResultClientId}>{item.clientId}</span>
                    {item.name && (
                      <span style={styles.deleteResultName}>{item.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {deleteResult.has_users.length > 0 && (
            <div style={styles.deleteResultSection}>
              <h4 style={{ ...styles.deleteResultSectionTitle, color: '#d97706' }}>
                ⚠ Could Not Delete - Has Active Users (
                {deleteResult.has_users.length})
              </h4>
              <div style={styles.deleteResultList}>
                {deleteResult.has_users.map((item, idx) => (
                  <div key={idx} style={styles.deleteResultItem}>
                    <span style={styles.deleteResultClientId}>{item.clientId}</span>
                    <span style={styles.deleteResultReason}>
                      {item.users_count} user(s)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {deleteResult.not_found.length > 0 && (
            <div style={styles.deleteResultSection}>
              <h4 style={{ ...styles.deleteResultSectionTitle, color: '#6b7280' }}>
                ○ Not Found ({deleteResult.not_found.length})
              </h4>
              <div style={styles.deleteResultList}>
                {deleteResult.not_found.map((item, idx) => (
                  <div key={idx} style={styles.deleteResultItem}>
                    <span style={styles.deleteResultClientId}>{item.clientId}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => setDeleteResult(null)} style={styles.dismissButton}>
            Dismiss
          </button>
        </div>
      )}

      {clientIds.length > 0 && !deleteResult && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>
              Client IDs ({clientIds.length})
            </h3>
            <button onClick={handleClear}
             //style={styles.clearButtonSmall}
             >
              Clear All
            </button>
          </div>

          <div style={styles.summaryBar}>
            <span>
              Total: <strong>{clientIds.length}</strong>
            </span>
            <span style={styles.summaryDivider}>|</span>
            <span style={{ color: '#10b981' }}>
              Individual: <strong>{uniqueCount}</strong>
            </span>
            <span style={styles.summaryDivider}>|</span>
            <span style={{ color: '#f59e0b' }}>
              Business: <strong>{duplicateCount}</strong>
            </span>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Client ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Users Limit</th>
                </tr>
              </thead>
              <tbody>
                {clientIds.slice(0, 100).map((item, index) => (
                  <tr key={`${item.clientId}-${index}`}>
                    <td style={styles.td}>{item.clientId}</td>
                    <td style={styles.tdMuted}>{item.name || '-'}</td>
                    <td style={styles.td}>
                      <span
                        // style={
                        //   item.type === 'Individual'
                        //     ? styles.badgeIndividual
                        //     : styles.badgeBusiness
                        // }
                      >
                        {item.type}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {item.occurrences > 1 ? (
                        <span style={styles.tdWarningText}>
                          {item.users_limit} ({item.occurrences}x)
                        </span>
                      ) : (
                        item.users_limit
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clientIds.length > 100 && (
              <div style={styles.moreText}>
                ...and {clientIds.length - 100} more
              </div>
            )}
          </div>

          <div style={styles.buttonGroup}>
           
            <CustomButton
                title={isLoading ? 'Uploading...' : ` Upload ${clientIds.length} Client IDs`}
                name="uploadPartnerClientIdsButton"
                type="submit"
                handleClick={() => {handleUpload()}}
                loading={isLoading}
                disabled={isLoading || !selectedBusinessId || isAnyLoading}
                variant="primary"
          />
           
            <CustomButton
                title={isDeleting ? 'Deleting...' : ` Delete ${clientIds.length} Client IDs`}
                name="deletePartnerClientIdsButton"
                type="submit"
                handleClick={() => {handleDelete()}}
                loading={isDeleting}
                disabled={isDeleting || !selectedBusinessId || isAnyLoading}
                variant="secondary"
          />
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: 24,
    maxWidth: 900,
    margin: '0 auto',
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 24,
    color: '#111',
  },
  field: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
    color: '#333',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: 16,
    border: '1px solid #ddd',
    borderRadius: 8,
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  uploadArea: {
    border: '2px dashed #ccc',
    borderRadius: 12,
    padding: 40,
    textAlign: 'center',
    marginBottom: 20,
    transition: 'border-color 0.2s, background-color 0.2s',
    backgroundColor: '#fafafa',
    cursor: 'pointer',
  },
  uploadIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  uploadText: {
    fontSize: 16,
    fontWeight: 500,
    color: '#333',
    marginBottom: 4,
  },
  uploadHint: {
    fontSize: 13,
    color: '#888',
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  success: {
    backgroundColor: '#f0fdf4',
    color: '#15803d',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  section: {
    padding: 20,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  summaryDivider: {
    color: '#cbd5e1',
  },
  clearButtonSmall: {
    background: 'none',
    border: '1px solid #dc2626',
    color: '#dc2626',
    padding: '6px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  tableContainer: {
    maxHeight: 400,
    overflowY: 'auto',
    overflowX: 'auto',
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    marginBottom: 16,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    borderBottom: '2px solid #e5e5e5',
    backgroundColor: '#f9f9f9',
    fontWeight: 600,
    fontSize: 13,
    position: 'sticky',
    top: 0,
  },
  td: {
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 14,
  },
  tdMuted: {
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 14,
    color: '#666',
  },
  tdWarningText: {
    color: '#d97706',
    fontWeight: 600,
  },
  moreText: {
    textAlign: 'center',
    padding: 12,
    color: '#666',
    fontSize: 13,
    backgroundColor: '#f9f9f9',
  },
  infoBox: {
    backgroundColor: '#f0f9ff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
    color: '#0369a1',
    lineHeight: 1.8,
  },
  buttonGroup: {
    display: 'flex',
    gap: 12,
  },
  uploadButton: {
    flex: 1,
    padding: 16,
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  deleteButton: {
    flex: 1,
    padding: 16,
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#dc2626',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
  },
  badgeIndividual: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeBusiness: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
  },
  // Delete Result Styles
  deleteResultContainer: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  deleteResultTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 20,
  },
  deleteStats: {
    display: 'flex',
    gap: 16,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  deleteStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    minWidth: 100,
  },
  deleteStatSuccess: {
    borderColor: '#10b981',
    backgroundColor: '#f0fdf4',
  },
  deleteStatWarning: {
    borderColor: '#f59e0b',
    backgroundColor: '#fffbeb',
  },
  deleteStatMuted: {
    borderColor: '#9ca3af',
    backgroundColor: '#f9fafb',
  },
  deleteStatError: {
    borderColor: '#dc2626',
    backgroundColor: '#fef2f2',
  },
  deleteStatNumber: {
    fontSize: 28,
    fontWeight: 700,
  },
  deleteStatLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  deleteResultSection: {
    marginBottom: 20,
  },
  deleteResultSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
    color: '#10b981',
  },
  deleteResultList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  deleteResultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    backgroundColor: '#fff',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    fontSize: 13,
  },
  deleteResultClientId: {
    fontWeight: 500,
  },
  deleteResultName: {
    color: '#6b7280',
  },
  deleteResultReason: {
    color: '#d97706',
    fontSize: 12,
  },
  dismissButton: {
    marginTop: 16,
    padding: '10px 20px',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
};